import { Editor } from "@tiptap/core";
import axios from "axios";
import { debounce } from "lodash";
import { useEffect, useRef } from "react";
import {
  collectProofBlocks,
  ProofBlock,
  ProofreadStorage,
} from "../components/Editor/ProofreadExtension";
import {
  lintParagraphs,
  MAX_PARAGRAPHS_PER_REQUEST,
} from "../services/proofread/api";

interface UseProofreaderOptions {
  enabled: boolean;
  /**
   * Anything that should invalidate cached results, e.g. the officer's
   * Common Spellings list. When it changes every paragraph is re-checked.
   */
  vocabularyKey: string;
}

// Gmail-style: wait for a pause in typing, then check what changed. Each
// paragraph is sent once per distinct text, so a paragraph the officer
// keeps editing costs one call per pause, not one per keystroke.
const DEBOUNCE_MS = 1000;
const MIN_WORDS = 4;
// After a 429 or server error, stay quiet for a bit rather than hammering.
const BACKOFF_MS = 60_000;

const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

/**
 * Drives the Proofread extension: after the officer pauses typing, send every
 * paragraph whose text differs from the baseline snapshot (the case as it
 * was loaded, so the AI draft is never underlined) and hasn't been checked at
 * that exact text before.
 */
export function useProofreader(
  editor: Editor | null,
  { enabled, vocabularyKey }: UseProofreaderOptions,
) {
  const lintedRef = useRef(new Set<string>());
  const inflightRef = useRef(new Set<string>());
  const backoffUntilRef = useRef(0);
  const runRef = useRef<(() => void) | null>(null);

  // Take the baseline once the editor exists. Editor.tsx also re-snapshots
  // after a server-driven content reset.
  useEffect(() => {
    if (!editor) return;
    editor.commands.setProofBaseline();
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.commands.setProofreadEnabled(enabled);
    if (!enabled) {
      editor.commands.clearProofSuggestions();
      lintedRef.current.clear();
    }
  }, [editor, enabled]);

  // Vocabulary changed (Add to dictionary, preferences saved): every result
  // is stale, so drop the cache and re-run.
  const firstVocabRun = useRef(true);
  useEffect(() => {
    if (!editor || !enabled) return;
    if (firstVocabRun.current) {
      firstVocabRun.current = false;
      return;
    }
    lintedRef.current.clear();
    editor.commands.clearProofSuggestions();
    runRef.current?.();
  }, [editor, enabled, vocabularyKey]);

  useEffect(() => {
    if (!editor || !enabled) return;
    let disposed = false;
    const controllers = new Set<AbortController>();

    const run = async () => {
      if (disposed || !editor.isEditable) return;
      if (Date.now() < backoffUntilRef.current) return;
      const storage = editor.storage.proofread as ProofreadStorage;
      const blocks = collectProofBlocks(editor.state.doc);
      const present = new Set(blocks.map((b) => b.hash));

      // Forget paragraphs that no longer exist at that exact text so a
      // reverted edit is checked again. Underlines themselves live in the
      // extension's decoration set and follow the text on their own.
      for (const hash of Array.from(lintedRef.current)) {
        if (!present.has(hash)) lintedRef.current.delete(hash);
      }

      const seen = new Set<string>();
      const todo: ProofBlock[] = blocks.filter((b) => {
        if (seen.has(b.hash)) return false;
        seen.add(b.hash);
        if (storage.baseline.has(b.hash)) return false;
        if (lintedRef.current.has(b.hash) || inflightRef.current.has(b.hash)) return false;
        if (wordCount(b.text) < MIN_WORDS) {
          // Too short to check, but remember it so we don't re-evaluate.
          lintedRef.current.add(b.hash);
          return false;
        }
        return true;
      });
      if (!todo.length) return;

      for (let i = 0; i < todo.length; i += MAX_PARAGRAPHS_PER_REQUEST) {
        sendBatch(todo.slice(i, i + MAX_PARAGRAPHS_PER_REQUEST));
      }
    };

    const sendBatch = (batch: ProofBlock[]) => {
      const controller = new AbortController();
      controllers.add(controller);
      batch.forEach((b) => inflightRef.current.add(b.hash));
      lintParagraphs(batch, controller.signal)
        .then((results) => {
          if (disposed || editor.isDestroyed) return;
          const live = new Set(collectProofBlocks(editor.state.doc).map((b) => b.hash));
          results.forEach((suggestions, hash) => {
            lintedRef.current.add(hash);
            // The officer kept typing; that text is gone, so its
            // suggestions would land on nothing.
            if (!live.has(hash)) return;
            editor.commands.setProofSuggestions(hash, suggestions);
          });
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          const status = axios.isAxiosError(err) ? err.response?.status : undefined;
          if (status === 429 || (status && status >= 500) || !status) {
            backoffUntilRef.current = Date.now() + BACKOFF_MS;
          }
          console.warn("[proofread] check failed", err);
        })
        .finally(() => {
          controllers.delete(controller);
          batch.forEach((b) => inflightRef.current.delete(b.hash));
        });
    };

    const scheduled = debounce(() => {
      void run();
    }, DEBOUNCE_MS);
    runRef.current = scheduled;
    editor.on("update", scheduled);
    scheduled();

    return () => {
      disposed = true;
      scheduled.cancel();
      editor.off("update", scheduled);
      controllers.forEach((c) => c.abort());
      runRef.current = null;
    };
  }, [editor, enabled]);
}
